import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TermsId, TermsItem } from './termsContent';
import { getSignupTerms, emptySignupAgreements, type SignupMemberType } from './signupTerms';
import { designSystem } from '@/styles/theme';

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    gap: 8px;
`;

const Left = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;

    .MuiFormControlLabel-root {
        flex: 1;
        min-width: 0;
        margin-right: 0;
    }

    .MuiFormControlLabel-label {
        word-break: keep-all;
        line-height: 1.4;
    }
`;

const RequiredBadge = styled.span`
    color: ${designSystem.colors.status.error};
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
    white-space: nowrap;
`;

const OptionalBadge = styled.span`
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    flex-shrink: 0;
    white-space: nowrap;
`;

const ViewButton = styled.button`
    background: none;
    border: none;
    color: ${designSystem.colors.primary};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        text-decoration: underline;
    }

    @media (max-width: 768px) {
        font-size: 13px;
        padding: 4px 4px;
    }
`;

const AllAgreeRow = styled(Row)`
    padding: 8px 0;
    .MuiFormControlLabel-label {
        font-weight: 600;
        font-size: 15px;
    }
`;

const ContentBody = styled.div`
    white-space: pre-wrap;
    line-height: 1.7;
    font-size: 14px;
    color: #1f2937;
    max-height: 60vh;
    overflow-y: auto;
`;

interface TermsProps {
    memberType?: SignupMemberType;
    onAllAgreed: (allRequiredAgreed: boolean) => void;
    onAgreementsChange?: (agreements: Record<TermsId, boolean>) => void;
}

const Terms = ({ onAllAgreed, onAgreementsChange, memberType = 'user' }: TermsProps) => {
    const terms = useMemo(() => getSignupTerms(memberType), [memberType]);
    const initial = useMemo(
        () => emptySignupAgreements(),
        []
    );
    const [agreed, setAgreed] = useState<Record<TermsId, boolean>>(initial);
    const [openId, setOpenId] = useState<TermsId | null>(null);

    const requiredIds = useMemo(() => terms.filter((t) => t.required).map((t) => t.id), [terms]);
    const allRequiredAgreed = requiredIds.every((id) => agreed[id]);
    const isAllAgreed = terms.every((t) => agreed[t.id]);

    const handleAllAgree = () => {
        const next = !isAllAgreed;
        const newState = terms.reduce(
            (acc, t) => ({ ...acc, [t.id]: next }),
            emptySignupAgreements()
        );
        setAgreed(newState);
    };

    const handleToggle = (id: TermsId) => {
        setAgreed((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    useEffect(() => {
        onAllAgreed(allRequiredAgreed);
        if (onAgreementsChange) onAgreementsChange(agreed);
    }, [agreed, allRequiredAgreed]);

    const opened: TermsItem | undefined = openId ? terms.find((t) => t.id === openId) : undefined;

    return (
        <>
            <AllAgreeRow>
                <FormControlLabel
                    control={<Checkbox checked={isAllAgreed} onChange={handleAllAgree} color="primary" />}
                    label="모든 약관에 동의합니다."
                />
            </AllAgreeRow>

            <Divider sx={{ my: 1 }} />

            {terms.map((t) => (
                <Row key={t.id}>
                    <Left>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={agreed[t.id]}
                                    onChange={() => handleToggle(t.id)}
                                />
                            }
                            label={t.title}
                        />
                        {t.required ? <RequiredBadge>(필수)</RequiredBadge> : <OptionalBadge>(선택)</OptionalBadge>}
                    </Left>
                    <ViewButton type="button" onClick={() => setOpenId(t.id)}>
                        내용보기
                    </ViewButton>
                </Row>
            ))}

            <Dialog open={!!opened} onClose={() => setOpenId(null)} maxWidth="md" fullWidth scroll="paper">
                {opened && (
                    <>
                        <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
                            {opened.title}
                            <IconButton
                                aria-label="close"
                                onClick={() => setOpenId(null)}
                                sx={{ position: 'absolute', right: 8, top: 8, color: '#6b7280' }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box sx={{ mb: 2, color: '#6b7280', fontSize: 14 }}>{opened.summary}</Box>
                            <ContentBody>{opened.content}</ContentBody>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default Terms;
